import React from 'react'
import { Button } from '@/components/ui/button'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import {
  CountryOverride,
  MarketingCampaignDetailDto,
} from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { UpdateCampaignCountryOverride } from 'api/Trading-Accounts/Types/UpdateCampaignCountryOverride'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface EditCountryOverrideFormValues {
  id: string | undefined
  countryId: string
  tickmillCompanyId: number
  minAmount: number
  enabled: boolean
}

const EditCountryOverrideFormUI: React.FC<
  FormikProps<EditCountryOverrideFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors } = props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Minimum Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='minAmount' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.minAmount && <FormBaseErrorMessage>{errors.minAmount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Tickmill Company</FormBaseFieldLabel>
            <Field component='select' name='tickmillCompanyId'>
              <option value=''>None</option>
              {props.tickmillCompanies.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Field>
          </FormBaseRow>
          {errors.tickmillCompanyId && (
            <FormBaseErrorMessage>{errors.tickmillCompanyId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Country</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='countryId'>
                <option value=''>None</option>
                {(props.countries || []).map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.countryId && <FormBaseErrorMessage>{errors.countryId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Enabled</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='enabled' type='checkbox' />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' type='button' onClick={handleSubmitForm}>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  country: CountryOverride
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]

  onSubmit(values: FormSubmitValues<UpdateCampaignCountryOverride>): Promise<void>
}

const dataToSubmit = (
  props: OuterProps,
  values: EditCountryOverrideFormValues
): FormSubmitValues<UpdateCampaignCountryOverride> => {
  const { data, tickmillCompanies = [], countries = [] } = props

  const country = countries.find((x) => x.id === values.countryId)
  const tickmillCompany = tickmillCompanies.find((x) => x.id === values.tickmillCompanyId)

  if (country?.id && tickmillCompany?.id && values?.id) {
    return {
      id: props.country?.id || null,
      campaignId: data.id,
      countryId: country.id,
      tickmillCompanyId: tickmillCompany.id,
      minAmount: values.minAmount,
      enabled: values.enabled,
    }
  }

  return {
    id: props.country?.id || null,
    campaignId: data.id,
    countryId: props.country.country.id,
    tickmillCompanyId: props.country.tickmillCompany.id,
    minAmount: props.country.minAmount,
    enabled: props.country.enabled,
  }
}

const initialState = (props: OuterProps) => {
  const { data, country } = props
  return {
    id: country?.id,
    campaignId: data.id,
    countryId: country.country.id,
    tickmillCompanyId: country.tickmillCompany.id,
    minAmount: country.minAmount,
    enabled: country.enabled,
  }
}

export const EditCountryOverrideForm = withFormik<OuterProps, EditCountryOverrideFormValues>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<EditCountryOverrideFormValues> = {}
    if (!values.countryId) {
      errors.countryId = 'required'
    }

    if (values.minAmount < 0) {
      errors.minAmount = 'Must be greater than 0.'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(EditCountryOverrideFormUI)
